import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowUpRight,
  Copy,
  Package,
  QrCode,
  RefreshCw,
  X,
} from 'lucide-react';
import type { UpdateState } from '../../shared/types';

export interface AboutDialogLinkItem {
  id: string;
  label: string;
  description: string;
  value: string;
  action: 'external' | 'copy' | 'disabled';
}

export interface AboutDialogQrItem {
  id: string;
  title: string;
  subtitle: string;
  hint: string;
  imageSrc?: string | null;
  copyValue?: string | null;
}

export interface AboutDialogProductItem {
  id: string;
  title: string;
  description: string;
  tag: string;
  url: string;
}

interface AboutDialogProps {
  open: boolean;
  appName: string;
  subtitle: string;
  brandIconSrc: string;
  buildChannelLabel: string;
  platformTags: string[];
  versionLabel: string;
  updateState: UpdateState;
  updateBusyAction: 'check' | 'install' | null;
  links: AboutDialogLinkItem[];
  qrItems: AboutDialogQrItem[];
  products: AboutDialogProductItem[];
  onOpenChange: (open: boolean) => void;
  onCheckForUpdates: () => void;
  onInstallUpdate: () => void;
  onOpenUpdateNotes: () => void;
  onOpenExternal: (url: string) => void;
  onCopyValue: (label: string, value: string) => void;
  onPreviewQr: (item: AboutDialogQrItem) => void;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '未记录';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '未记录';
  }

  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const mapUpdateStatusLabel = (updateState: UpdateState): string => {
  switch (updateState.availability) {
    case 'checking':
      return '检查中';
    case 'available':
      return '发现新版本';
    case 'downloading':
      return '下载中';
    case 'ready':
      return updateState.currentPlatform === 'win' ? '安装器已就绪' : '更新包已下载';
    case 'required':
      return '必须更新';
    case 'up-to-date':
      return '已是最新';
    case 'error':
      return '更新失败';
    case 'unconfigured':
      return '未配置';
    default:
      return '待检查';
  }
};

export function AboutDialog({
  open,
  appName,
  brandIconSrc,
  buildChannelLabel,
  platformTags,
  versionLabel,
  updateState,
  updateBusyAction,
  links,
  qrItems,
  products,
  onOpenChange,
  onCheckForUpdates,
  onInstallUpdate,
  onOpenUpdateNotes,
  onOpenExternal,
  onCopyValue,
  onPreviewQr,
}: AboutDialogProps) {
  const updateStatusLabel = mapUpdateStatusLabel(updateState);
  const updateNote =
    updateState.errorMessage || updateState.notesContent || updateState.manifest?.notes || '';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="shell-dialog-overlay" />
        <Dialog.Content className="shell-dialog-panel shell-dialog-panel--about">
          <header className="shell-dialog-header">
            <div>
              <Dialog.Title className="shell-dialog-title">关于与更新</Dialog.Title>
              <Dialog.Description className="sr-only">
                这里展示版本信息、更新入口、联系方式和相关网站。
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="shell-dialog-close shell-dialog-close--plain"
                aria-label="关闭关于窗口"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </header>

          <div className="shell-dialog-body shell-dialog-body--about about-dialog-body">
            <section className="about-hero-card about-hero-card--minimal">
              <div className="about-hero-card__icon about-hero-card__icon--brand">
                <img src={brandIconSrc} alt="" className="about-hero-card__brand-icon" />
              </div>
              <div className="about-hero-card__content">
                <h2 className="about-hero-card__title">{appName}</h2>
                <div className="about-hero-card__meta">
                  <span className="badge badge--role-current">{buildChannelLabel}</span>
                  <span className="badge badge--manual">{versionLabel}</span>
                  {platformTags.map((tag) => (
                    <span key={tag} className="badge badge--plan">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            <section className="about-plain-section">
              <div className="about-plain-section__header">
                <h3>更新状态</h3>
                <span
                  className={`about-status-badge${
                    updateState.blockedByMandatory
                      ? ' is-danger'
                      : updateState.updateAvailable
                        ? ' is-active'
                        : ''
                  }`}
                >
                  {updateStatusLabel}
                </span>
              </div>

              <div className="about-metric-grid about-metric-grid--compact">
                <div className="about-metric-card about-metric-card--compact">
                  <span className="about-metric-card__label">当前版本</span>
                  <strong className="about-metric-card__value">{updateState.currentVersion}</strong>
                </div>
                <div className="about-metric-card about-metric-card--compact">
                  <span className="about-metric-card__label">最新版本</span>
                  <strong className="about-metric-card__value">
                    {updateState.manifest?.version ?? '未检查'}
                  </strong>
                </div>
                <div className="about-metric-card about-metric-card--compact">
                  <span className="about-metric-card__label">最近检查</span>
                  <strong className="about-metric-card__value">
                    {formatDateTime(updateState.lastCheckedAt)}
                  </strong>
                </div>
                <div className="about-metric-card about-metric-card--compact">
                  <span className="about-metric-card__label">更新来源</span>
                  <strong className="about-metric-card__value">
                    {updateState.sourceName ? updateState.sourceName.toUpperCase() : '未检查'}
                  </strong>
                </div>
              </div>

              <div className="about-update-actions about-update-actions--inline">
                <button type="button" className="sidebar-button" onClick={onCheckForUpdates}>
                  {updateBusyAction === 'check' ? (
                    <RefreshCw size={15} className="spin" />
                  ) : (
                    <RefreshCw size={15} />
                  )}
                  检查更新
                </button>
                <button
                  type="button"
                  className="sidebar-button sidebar-button--secondary"
                  onClick={onInstallUpdate}
                  disabled={!updateState.updateAvailable}
                >
                  <Package size={15} />
                  {updateState.currentPlatform === 'win' && updateState.downloadFilePath
                    ? '开始安装更新'
                    : updateState.currentPlatform === 'win'
                      ? '立即更新'
                      : '下载更新包'}
                </button>
                {updateNote ? (
                  <button
                    type="button"
                    className="sidebar-button sidebar-button--secondary"
                    onClick={onOpenUpdateNotes}
                  >
                    <ArrowUpRight size={14} />
                    查看说明
                  </button>
                ) : null}
              </div>
            </section>

            <section className="about-plain-section">
              <div className="about-plain-section__header">
                <h3>联系方式</h3>
              </div>

              <div className="about-link-strip">
                {links.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="about-link-strip__item"
                    onClick={() => {
                      if (item.action === 'external') {
                        onOpenExternal(item.value);
                        return;
                      }

                      if (item.action === 'copy') {
                        onCopyValue(item.label, item.value);
                      }
                    }}
                    disabled={item.action === 'disabled'}
                  >
                    <span>{item.label}</span>
                  </button>
                ))}
                {qrItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="about-link-strip__item about-link-strip__item--qr"
                    onClick={() => onPreviewQr(item)}
                  >
                    <QrCode size={14} />
                    <span>{item.title}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="about-plain-section">
              <div className="about-plain-section__header">
                <h3>相关网站</h3>
              </div>

              <div className="about-related-links">
                {products.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    className="about-related-links__item"
                    onClick={() => onOpenExternal(product.url)}
                  >
                    <span>{product.title}</span>
                    <ArrowUpRight size={14} />
                  </button>
                ))}
              </div>
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
